import { createPredicate } from './condition';
import { BrandclubEventType, TrackingPixel } from './types';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

let debug = false;
let initialized = false;
let loaderScript: HTMLScriptElement | null = null;
let initScript: HTMLScriptElement | null = null;

const PROVIDER = 'GoogleAds';

const log = (...args: any[]) => {
  if (!debug) {
    return;
  }
  // eslint-disable-next-line no-console
  console.info(...['[ga]'].concat(args));
};
function initializeGoogleAds(brandId: string, tagId: string, options?: any) {
  if (!initialized && brandId && tagId) {
    initialized = true;
    loaderScript = document.createElement('script');
    loaderScript.async = true;
    loaderScript.type = 'text/javascript';
    loaderScript.src = `https://www.googletagmanager.com/gtag/js?id=${tagId}`;
    document.head.appendChild(loaderScript);
    initScript = document.createElement('script');
    initScript.innerHTML = `window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${tagId}' ${options ? ',' + JSON.stringify(options) : ''});`;
    document.head.appendChild(initScript);
  }
}

function reportConversion({
  googleTagId,
  conversionId,
  value,
  currency,
}: {
  googleTagId: string;
  conversionId: string;
  value: number;
  currency: string;
}) {
  window.gtag('event', 'conversion', {
    send_to: `${googleTagId}/${conversionId}`,
    value,
    currency,
  });
  return false;
}

const googleTags = {
  enableDebug() {
    debug = true;
  },
  track({
    brandId,
    pixels,
    eventName,
    eventType,
    eventPayload
  }: {
    brandId: string;
    pixels: TrackingPixel[];
    eventName: string;
    eventType: BrandclubEventType;
    eventPayload: any;
  }) {
    pixels = pixels?.filter((item) => item.pixelProvider === PROVIDER);
    if (!pixels?.length) {
      return;
    }
    pixels.forEach((pixel) => {
      if (pixel?.pixelId) {
        if (pixel.eventFilter) {
          const predicate = createPredicate(pixel.eventFilter);
          if (!predicate(eventPayload)) {
            log('eventFilter not matched', pixel.eventFilter, pixel?.pixelId, eventName, eventPayload);
            return;
          }
        }
        let conversionId = '';
        if (eventType === BrandclubEventType.PageView) {
          conversionId = pixel?.pageViewConversionId;
        } else if (eventType === BrandclubEventType.PurchaseConfirmed || eventType === BrandclubEventType.AddToCart) {
          conversionId = pixel?.buyBoxClickConversionId;
        }
        if (conversionId) {
          initializeGoogleAds(brandId, pixel?.pixelId, pixel?.pixelOptions);
          reportConversion({
            googleTagId: pixel.pixelId,
            conversionId,
            value: eventPayload?.retailPrice || 0,
            currency: 'USD',
          });
          log('conversion reported', conversionId, eventName, eventPayload);
        } else {
          log('conversionId not found', eventName, eventPayload);
        }
      }
    });
  },

  grantConsent() {
    if (!initialized || typeof window === 'undefined' || !window.gtag) {
      log('Cannot grant consent - Google Ads not initialized');
      return;
    }

    window.gtag('consent', 'update', {
      'analytics_storage': 'granted',
      'ad_storage': 'granted',
      'ad_user_data': 'granted',
      'ad_personalization': 'granted'
    });
    log('Google Ads consent granted');
  },

  revokeConsent() {
    if (!initialized || typeof window === 'undefined' || !window.gtag) {
      log('Cannot revoke consent - Google Ads not initialized');
      return;
    }

    window.gtag('consent', 'update', {
      'analytics_storage': 'denied',
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied'
    });
    log('Google Ads consent revoked');
  },
};

export default googleTags;
